<template>
  <div class="min-h-screen bg-white dark:bg-[#0c1427] p-4 sm:p-6 lg:p-8">
    <!-- Top Metrics Grid -->
    <div class="mb-8 sm:mb-12">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 auto-rows-fr">
        <!-- Revenue Growth Chart -->
        <div class="group animate-fade-in-up transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 h-full" style="animation-delay: 0.1s">
          <RevenueGrowthChart />
    </div>

        <!-- Annual Profit Chart -->
        <div class="group animate-fade-in-up transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 h-full" style="animation-delay: 0.2s">
          <AnnualProfitChart />
      </div>

        <!-- Lead Conversion Chart -->
        <div class="group animate-fade-in-up transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 h-full w-full" style="animation-delay: 0.3s">
          <LeadConversionChart />
        </div>
      </div>
    </div>

    <!-- Balance Overview -->
    <div class="mb-8 sm:mb-12">
      <div class="relative group animate-fade-in-up transition-all duration-500 hover:scale-[1.01] hover:-translate-y-1" style="animation-delay: 0.4s">
          <BalanceOverviewChart />
      </div>
    </div>

    <!-- Recent Leads Section -->
    <div class="animate-fade-in-up transition-all duration-500" style="animation-delay: 0.5s">
        <RecentLeads />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import BalanceOverviewChart from "@/components/Dashboard/CRM/BalanceOverviewChart.vue";
import LeadConversionChart from "@/components/Dashboard/CRM/LeadConversionChart.vue";
import AnnualProfitChart from "@/components/Dashboard/CRM/AnnualProfitChart.vue";
import RevenueGrowthChart from "@/components/Dashboard/CRM/RevenueGrowthChart.vue";
import RecentLeads from "@/components/Dashboard/CRM/RecentLeads.vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "CRMPage",
  components: {
    BalanceOverviewChart,
    LeadConversionChart,
    AnnualProfitChart,
    RevenueGrowthChart,
    RecentLeads,
  },
  setup() {
    const { t, locale } = useI18n();

    // Dynamic direction based on language
    const isRtl = computed(() => ['he', 'ar'].includes(locale.value));



    return {
      t,
      isRtl,
    };
  },
});
</script>

<style scoped>
/* Custom Animations */
@keyframes fade-in-down {
  from {
  opacity: 0;
    transform: translateY(-30px);
}
  to {
  opacity: 1;
    transform: translateY(0);
}
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
}
  to {
    opacity: 1;
    transform: translateY(0);
}
}

@keyframes fade-in-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
}
  to {
    opacity: 1;
    transform: translateX(0);
}
}

@keyframes fade-in-right {
  from {
    opacity: 0;
    transform: translateX(30px);
}
  to {
    opacity: 1;
    transform: translateX(0);
}
}

@keyframes bounce-in {
  0% {
    transform: scale(0) rotate(180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(90deg);
    opacity: 0.8;
}
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
}
}

.animate-fade-in-down {
  animation: fade-in-down 0.8s ease-out forwards;
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

.animate-fade-in-left {
  animation: fade-in-left 0.8s ease-out forwards;
  opacity: 0;
}

.animate-fade-in-right {
  animation: fade-in-right 0.8s ease-out forwards;
  opacity: 0;
}

.animate-bounce-in {
  animation: bounce-in 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  opacity: 0;
}

/* Glassmorphism Effect Enhancement */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
  }
  
/* Mobile Optimizations */
@media (max-width: 640px) {
  .backdrop-blur-sm {
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .bg-gradient-to-br {
    background: linear-gradient(to bottom right, #0f172a, #1e293b, #334155);
  }
}
</style>

